import React, { useState, useEffect } from "react";
import {
  definePluralOrSingular,
  randomInteger,
  fetchCelebrations,
} from "./Recommendations";

const Celebrate = ({
  selectedRecommendationTab,
  emotionPercents,
  actionsCounter,
  studentDataLength,
  actionCounts,
  filteredDataPoints,
  allStudents,
  chosenTime,
  studentEmailToNameMap,
  state,
}) => {
  // Define required state variables
  const [insights, setInsights] = useState("");
  const [recommendationType, setRecommendationType] = useState("");
  const [applicableEmotion, setApplicableEmotion] = useState("");
  const [pluralOrSingular, setPluralOrSingular] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [considerations, setConsiderations] = useState("");
  const [emotion_percent, setEmotionPercent] = useState("");

  let chosenTimeLabels = {
    "10 minutes": "Last 10 minutes",
    "1 hour": "Last 1 hour",
    "2 hours": "Last 2 hours",
    "24 hours": "Last 24 hours",
    week: "week",
    month: "month",
    "3 months": "Last 3 months",
    "6 months": "Last 6 months",
    year: "year",
    "2 years": "Last 2 years",
  };

  // Calculate Number of Wellness Points
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };
  const formattedCounter = formatNumber(actionsCounter);

  const getApplicableCelebration = () => {
    if (studentDataLength === 0) {
      return "--";
    } else if (studentDataLength <= 20) {
      return "LessThan20Points";
    } else {
      return "MoreThan20Points";
    }
  };

  const defineCelebrationType = () => {
    const randomNumber = randomInteger(0, 100);
    if (randomNumber >= 0 && randomNumber < 50) {
      return "Conditional";
    } else {
      return "Random";
    }
  };

  const getVariablesBetweenHashtags = (text) => {
    const regex = /#(\w+)#/g;
    const match = text.match(regex);
    return match;
  };

  const replaceBetweenHashtags = (text) => {
    // Find the word between two #
    const regex = /#(\w+)#/g;
    const match = text.match(regex);

    // IF there is no match return
    if (!match) {
      return text;
    }

    // Find variable name the same and get the value
    const variableName = match[0].replace(/#/g, ""); // Remove the # symbols
    const value = { actionsCounter, studentDataLength }[variableName]; // Access the prop directly

    // Replace the # with the value
    const replacedText = text.replace(`#${variableName}#`, value);
    return replacedText;
  };

  const condition1 = () => {
    // Check if emotionPercents contains ALL challenging emotions Anger and Sadness i.e angry and sad
    // AND their current values are greater than 0
    const hasChallangingEmotions = ["angry", "sad"].every((requiredEmotion) =>
      emotionPercents.some(
        (emotion) =>
          emotion.name.toLowerCase() === requiredEmotion.toLowerCase() &&
          emotion.current > 0
      )
    );
    return { status: hasChallangingEmotions, data: {} };
  };

  const condition2 = () => {
    // Check that student has experienced ANY of the following emotions and found helpful actions

    const experiencedEmotions = ["angry", "sad", "lonely"].filter(
      (requiredEmotion) =>
        filteredDataPoints.some(
          (dataPoint) =>
            dataPoint.emotion.toLowerCase() === requiredEmotion.toLowerCase() &&
            dataPoint.helpful
        )
    );

    const hasChallengingEmotionsAndHelpfulActions =
      experiencedEmotions.length > 0;

    return {
      status: hasChallengingEmotionsAndHelpfulActions,
      data: {
        experienced_emotions: experiencedEmotions.join(", "),
      },
    };
  };

  const condition3 = () => {
    // Check if emotionPercents contains 3+ different emotions where the currrent is greater than 0
    const positiveEmotions = emotionPercents.filter(
      (emotion) => emotion.current > 0
    );
    if (positiveEmotions.length >= 3) {
      // get names of first three emotions
      const firstThreeEmotions = positiveEmotions
        .slice(0, 3)
        .map((emotion) => emotion.name);
      return {
        status: true,
        data: { courage_emotions: `(${firstThreeEmotions.join(", ")})` },
      };
    } else {
      return { status: false, data: {} };
    }
  };

  const condition4 = () => {
    // Check if action data contains either helping others or give a compliment
    const hasHelpfulActions = [
      "Give someone a compliment",
      "Help Others",
    ].every((action) =>
      actionCounts.some(
        (actionCount) =>
          actionCount.name.toLowerCase() === action.toLowerCase() &&
          actionCount.current > 0
      )
    );
    return { status: hasHelpfulActions, data: {} };
  };

  const condition5 = () => {
    // Check if action data contains either stretching or moving
    const hasMovingActions = ["Stretch", "Move my body"].every((action) =>
      actionCounts.some(
        (actionCount) =>
          actionCount.name.toLowerCase() === action.toLowerCase() &&
          actionCount.current > 0
      )
    );
    return { status: hasMovingActions, data: {} };
  };

  const condition6 = () => {
    const seen = new Set();
    const uniqueStudentEmails = [];

    for (const dataPoint of filteredDataPoints) {
      const email = dataPoint?.email;
      const action = dataPoint?.action;

      if (
        email &&
        action?.toLowerCase().startsWith("other") &&
        !seen.has(email)
      ) {
        seen.add(email);
        uniqueStudentEmails.push(email);
      }

      if (uniqueStudentEmails.length >= 3) break;
    }

    if (uniqueStudentEmails.length === 0) {
      return { status: false, data: {} };
    }

    const studentNames = uniqueStudentEmails.map(
      (email) => studentEmailToNameMap[email] || email
    );

    return { status: true, data: { studentNames: studentNames.join(", ") } };
  };

  const condition7 = () => {
    const seen = new Set();
    const uniqueStudentEmails = [];

    for (const dataPoint of filteredDataPoints) {
      const email = dataPoint?.email;
      const action = dataPoint?.action;

      if (
        email &&
        action?.includes("Think of 3 things that I am grateful for") &&
        !seen.has(email)
      ) {
        seen.add(email);
        uniqueStudentEmails.push(email);
      }

      if (uniqueStudentEmails.length >= 3) break;
    }

    if (uniqueStudentEmails.length === 0) {
      return { status: false, data: {} };
    }

    const studentNames = uniqueStudentEmails.map(
      (email) => studentEmailToNameMap[email] || email
    );

    return { status: true, data: { studentNames: studentNames.join(", ") } };
  };

  const condition8 = () => {
    // Check if actionCounts contains 3+ different actions where the currrent is greater than 0
    const differentActions = actionCounts.filter(
      (action) => action.current > 0
    );
    if (differentActions.length >= 3) {
      // get names of first three emotions
      const firstThreeActions = differentActions
        .slice(0, 3)
        .map((action) => action.name);
      return {
        status: true,
        data: {
          action1: firstThreeActions[0],
          action2: firstThreeActions[1],
          action3: firstThreeActions[2],
        },
      };
    } else {
      return { status: false, data: {} };
    }
  };

  const condition9 = () => {
    // Check if emotionPercents contains 3+ different emotions where the currrent is greater than 0
    const differentEmotions = emotionPercents.filter(
      (emotion) => emotion.current > 0
    );
    if (differentEmotions.length >= 3) {
      // get names of first three emotions
      const firstThreeEmotions = differentEmotions
        .slice(0, 3)
        .map((emotion) => emotion.name);
      return {
        status: true,
        data: {
          emotion1: firstThreeEmotions[0],
          emotion2: firstThreeEmotions[1],
          emotion3: firstThreeEmotions[2],
        },
      };
    } else {
      return { status: false, data: {} };
    }
  };

  const condition10 = () => {
    // Get the total number of actions that were helpful in the studentData
    const numberOfHelpfulAcions = formatNumber(
      filteredDataPoints.filter((action) => action.helpful).length
    );
    return {
      status: true,
      data: { numberOfHelpfulAcions: numberOfHelpfulAcions },
    };
  };

  const MAX_ATTEMPTS = 20; // or whatever number makes sense
  let attempts = 0;
  const processInsights = (insights, type) => {
    try {
      if (type === "Random") {
        let original_insights = insights.insights;
        let random_insight =
          original_insights[randomInteger(0, original_insights.length - 1)];
        let replacedText = replaceBetweenHashtags(random_insight);

        // Replace the variable with the value
        replacedText = replacedText.replace(
          `#chosenTime#`,
          chosenTimeLabels[chosenTime]
        );

        // Set Insights
        setInsights(replacedText);
      } else {
        // Get all insights
        const allInsights = insights.insights;
        let foundValidInsight = false;

        // Map insight condition names to their check functions (replaces eval)
        const conditionFns = {
          condition1,
          condition2,
          condition3,
          condition4,
          condition5,
          condition6,
          condition7,
          condition8,
          condition9,
          condition10,
        };

        // Try each insight until we find one with a true condition
        while (!foundValidInsight && attempts < MAX_ATTEMPTS) {
          const random_insight =
            allInsights[randomInteger(0, allInsights.length - 1)];

          // Check if random_insight condition is true
          const condition_as_string = random_insight.condition;
          const condition_function = conditionFns[condition_as_string];
          if (typeof condition_function !== "function") {
            // Unknown condition name — skip this insight and try another
            attempts++;
            continue;
          }
          const condition_details = condition_function();

          if (condition_details.status) {
            foundValidInsight = true;

            // Check all varibles between hashtags
            const variables = getVariablesBetweenHashtags(
              random_insight.insight
            );
            if (!variables) {
              setInsights(random_insight.insight);
              break;
            }

            // Replace the variables with the values
            let replacedText = random_insight.insight;

            variables.forEach((variable) => {
              // Remove hashtags from the variable
              const variable_without_hashtags = variable.replace(/#/g, "");

              // get insights
              const insight_variables = random_insight.variables;
              const current_variable_details =
                insight_variables[variable_without_hashtags];

              // Check the emotion type
              if (
                current_variable_details.type === "condition_function_variables"
              ) {
                const current_variable_name = current_variable_details.value;

                // Get current variable name value from condition details
                const current_variable_name_value =
                  condition_details.data[current_variable_name];

                // Replace the variable with the value
                replacedText = replacedText.replace(
                  `#${current_variable_name}#`,
                  current_variable_name_value
                );

                setInsights(replacedText);
              } else if (current_variable_details.type === "state_based") {
                const current_variable_name = current_variable_details.value;

                // Get current variable name value from condition details
                const current_variable_name_value =
                  condition_details.data[current_variable_name];

                if (current_variable_name === "chosenTime") {
                  // Replace the variable with the value
                  replacedText = replacedText.replace(
                    `#chosenTime#`,
                    chosenTimeLabels[chosenTime]
                  );
                  setInsights(replacedText);
                } else {
                  // handle this condition later
                }
              } else {
                // Handle this condition later
              }
            });

            break;
          }
          attempts++;
        }

        // If no valid insight was found, set default message
        if (!foundValidInsight) {
          setInsights("--");
        }
      }
    } catch (error) {
      setInsights("--");
    }
  };

  const getRecommendations = async () => {
    try {
      // Set is loading to true
      // setIsLoading(true);

      // Get all values synchronously
      const applicableCelebration = getApplicableCelebration();
      if (applicableCelebration === "--") {
        setInsights("--");
        return;
      }

      const type = defineCelebrationType();

      // Define weather plural or singular
      let isPlural = true;
      if (
        state.chosenStudent &&
        state.chosenStudent != "All Students" &&
        state.chosenStudent != "Choose Student"
      ) {
        isPlural = false;
      }
      const plural_or_singular = definePluralOrSingular(isPlural);

      // Get and Process Insights
      const celebrationsResponse = await fetchCelebrations(
        applicableCelebration,
        type,
        plural_or_singular,
        selectedRecommendationTab
      );
      processInsights(celebrationsResponse, type);
    } catch (error) {
      setInsights("An error occurred while getting Insights/Considerations.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getRecommendations();
  }, [studentDataLength, actionsCounter]);

  return (
    <div className="flex flex-col gap-4 justify-center h-full">
      <div className="border-l-4 border-[#005BE2] pl-4">
        <h5 className="text-[12px] text-[#005BE2] font-semibold mb-2">
          CONGRATULATIONS!
        </h5>
        <p className="text-400 text-[16px] text-normal">{insights}</p>
      </div>
    </div>
  );
};

export default Celebrate;
